import { useLingui } from '@lingui/react'
import classNames from 'clsx'
import { arrayOf, bool, func, node, number, shape, string } from 'prop-types'

import { PAGE_TYPE_CATEGORY, PAGE_TYPE_SEARCH_RESULTS } from 'shared/consts'
import {
  NOW_CATEGORY_LABEL_CLICK,
  NOW_SERP_LABEL_CLICK,
} from 'shared/experiments/consts'
import { isRebrandingCOMActive } from 'shared/experiments/utils/rebranding'
import trackOptimizelyEvent from 'shared/experiments/utils/trackOptimizelyEvent'
import { isMobile } from 'shared/utils/deviceClassUtils'
import { isEmpty } from 'shared/utils/objectUtils'
import ButtonAsLink from 'views/components/molecules/ButtonAsLink'
import { NOW_SHIPPING_MODAL_ID } from 'views/components/organisms/NowShippingModal/names'
import { PROMOTION_TYPES } from 'views/components/organisms/ProductLabels/consts'
import { getPromotionLabelText } from 'views/components/organisms/ProductLabels/utils'
import {
  CLASS_PRODUCT_LABELS_MOBILE,
  PRESCRIPTION_INFO_MODAL_HOME_ONE_ID,
} from 'views/consts'
import { OPEN_NOW_SHIPPING_MODAL } from 'views/containers/browser/NowShippingModalContainer/clientsideHook'
import useShopAndRuntimeConfig from 'views/enhancers/useShopAndRuntimeConfig'
import { trackingUrls } from 'views/utils/propTypes/adserver'
import { PromotionProductTypeV2 } from 'views/utils/propTypes/api'
import { shouldRenderLabel } from 'views/utils/redPointsUtils'
import { usePublicConfig } from 'views/utils/shopAndRuntimeConfigUtils'

import { decorateLabelsWithExtraMobileClasses } from './helpers'
import SearchProductLabel from './SearchProductLabel'

const SearchProductLabels = ({
  discountLabel,
  displayAboLabel,
  displayNowWidget,
  handleProductAboLabelClick,
  mobileLabelsInitialNumber,
  otherLabels,
  offerTypeSae,
  prescription,
  promotion,
  promotionProduct,
  sponsored,
  sponsoredProductLabel,
  showDiscountLabel,
  redPointsData,
  upid,
  trackingUrls,
  additionalClasses,
}) => {
  const { i18n } = useLingui()
  let firstFound = false
  const labels = []

  const {
    publicRuntimeConfig: {
      pageProperties: { experiments = [], pageType },
    },
  } = useShopAndRuntimeConfig()

  const isRebrandingCOMEnabled = isRebrandingCOMActive(experiments)

  const itemClass =
    'u-vertical-align--top u-margin-x-small--bottom u-margin-x-small--right o-SearchProductListItem__label-item'

  if (showDiscountLabel) {
    labels.push(
      <li key="discount-label" className={itemClass}>
        <SearchProductLabel
          label={discountLabel}
          color="white"
          bgcolor="red"
          bordercolor="red"
          firstFound={firstFound}
          bolder
        />
      </li>
    )

    firstFound = true
  }

  if (prescription) {
    const prescriptionClassnames = classNames(itemClass, {
      'prescription__label-rebranding-com': isRebrandingCOMEnabled,
    })

    labels.push(
      <li key="prescription-label" className={prescriptionClassnames}>
        <ButtonAsLink
          type="button"
          name="productLabelPrescription"
          onClick={() => {
            trackOptimizelyEvent(event)
          }}
          className="o-SearchProductListItem__button-label o-ProductLabels__prescription-label"
          data-a11y-dialog-show={PRESCRIPTION_INFO_MODAL_HOME_ONE_ID}
          data-product-identifier={upid}
          isClientsideControlled
        >
          <SearchProductLabel
            label={i18n._('prescription.labels.prescriptionRequired')}
            color="positive"
            bgcolor="tertiary"
            showInfoIcon
            firstFound={firstFound}
          />
        </ButtonAsLink>
      </li>
    )

    if (!firstFound) {
      firstFound = true
    }
  }

  const EVENT_MAP = {
    [PAGE_TYPE_SEARCH_RESULTS]: NOW_SERP_LABEL_CLICK,
    [PAGE_TYPE_CATEGORY]: NOW_CATEGORY_LABEL_CLICK,
  }
  const event = EVENT_MAP[pageType]
  const nowButtonClassnames = classNames(
    itemClass,
    'o-SearchProductListItem__label-item--now-button'
  )

  labels.push(
    <li
      key="now-label"
      className={nowButtonClassnames}
      aria-hidden={!displayNowWidget || prescription}
    >
      <ButtonAsLink
        type="button"
        name="productLabelNow"
        onClick={() => {
          trackOptimizelyEvent(event)
        }}
        className="o-SearchProductListItem__button-label"
        data-clientside-hook={OPEN_NOW_SHIPPING_MODAL}
        data-a11y-dialog-show={NOW_SHIPPING_MODAL_ID}
        data-product-identifier={upid}
        isClientsideControlled
      >
        <SearchProductLabel
          label="NOW!"
          color="positive"
          bgcolor="background"
          bordercolor="positive"
          firstFound={firstFound}
        />
      </ButtonAsLink>
    </li>
  )

  if (displayNowWidget && !firstFound) {
    firstFound = true
  }

  labels.push(
    <li
      key="abo-label"
      className={itemClass}
      data-a11y-dialog-show="aboModal"
      data-clientside-hook="productLabelAbo"
      aria-hidden={!displayAboLabel ? true : null}
    >
      <ButtonAsLink
        type="button"
        onClick={handleProductAboLabelClick}
        name="productLabelAbo"
        className="o-SearchProductListItem__button-label"
      >
        <SearchProductLabel
          label={i18n._('product.label.availableForAbo')}
          color="blue"
          bgcolor="background"
          bordercolor="blue"
          firstFound={firstFound}
        />
      </ButtonAsLink>
    </li>
  )

  if (displayAboLabel && !firstFound) {
    firstFound = true
  }

  if (redPointsData && shouldRenderLabel(redPointsData)) {
    const redPointsClassnames = classNames(
      itemClass,
      'o-SearchProductListItem__label-item--red-points'
    )

    labels.push(
      <li key="red-points--label" className={redPointsClassnames}>
        <SearchProductLabel
          label={`${redPointsData.multiplierRP}x ${i18n._(
            'general.label.redPoints'
          )}`}
          bgcolor="background"
          bordercolor="red"
          color="red"
          dataQaId={redPointsData.multiplierRP}
          firstFound={firstFound}
        />
      </li>
    )

    if (!firstFound) {
      firstFound = true
    }
  }

  if (promotion) {
    let labelText
    let promotionClassNames = itemClass
    switch (promotion) {
      case PROMOTION_TYPES.ONE_FOR_ONE:
      case PROMOTION_TYPES.ONE_PLUS_ONE:
        labelText = i18n._('general.label.product.oneforone')
        break
      case PROMOTION_TYPES.BUNDLE:
        promotionClassNames = classNames(
          itemClass,
          'o-SearchProductListItem__label-item--bundle-label'
        )
        // legacy bundle promotion label
        labelText = getPromotionLabelText(promotion, promotionProduct)
        // fallback
        if (!labelText) {
          labelText = i18n._('general.label.product.bundle')
        }
        break
      case PROMOTION_TYPES.FREEBIE:
        labelText = i18n._('general.label.product.freebie')
        break
      case PROMOTION_TYPES.OUT_OF_TRADE:
        labelText = i18n._('general.label.product.outOfTrade')
        break
      default:
        labelText = null
    }

    if (labelText && offerTypeSae) {
      labels.push(
        <li key="promotion-label" className={promotionClassNames}>
          <SearchProductLabel
            label={labelText}
            color="dark"
            bgcolor="greySky"
            bordercolor="greySky"
            firstFound={firstFound}
          />
        </li>
      )

      if (!firstFound) {
        firstFound = true
      }
    }
  }

  if (sponsored) {
    labels.push(
      <li key="sponsored-label" className={itemClass}>
        <SearchProductLabel
          label={sponsoredProductLabel || i18n._('general.label.sponsored')}
          color="grey"
          bgcolor="background"
          bordercolor="grey"
          firstFound={firstFound}
        />
      </li>
    )

    if (!firstFound) {
      firstFound = true
    }
  }

  // Show “sponsored” label if we have trackingUrls, but don’t show if we already shown the label with default text
  if (
    trackingUrls &&
    !isEmpty(trackingUrls) &&
    (!sponsored || sponsoredProductLabel)
  ) {
    labels.push(
      <li key="sponsored-label" className={itemClass}>
        <SearchProductLabel
          label={i18n._('general.label.sponsored')}
          color="grey"
          bgcolor="background"
          bordercolor="grey"
          firstFound={firstFound}
        />
      </li>
    )

    if (!firstFound) {
      firstFound = true
    }
  }

  if (otherLabels?.length) {
    // For tenant IT or FR we get a lot of duplicate labels from Algolia
    // Check SAEFRNT-4402 for more info
    Array.from(new Set(otherLabels)).forEach((label, index) => {
      const key = `product-label-${index}`

      labels.push(
        <li key={key} className={itemClass}>
          <SearchProductLabel
            label={label}
            color="dark"
            bgcolor="greySky"
            bordercolor="greySky"
            firstFound={firstFound}
          />
        </li>
      )

      if (!firstFound) {
        firstFound = true
      }
    })
  }

  if (labels?.length) {
    labels.push(
      <li
        key="product-label-counter"
        className={`${itemClass} u-hidden`}
        data-clientside-hook="productLabelCounter"
      >
        <SearchProductLabel
          label=""
          color="dark"
          bgcolor="greySky"
          bordercolor="greySky"
          firstFound
        />
      </li>
    )
  }

  const { deviceClass } = usePublicConfig()
  const isMobileDevice = isMobile(deviceClass)
  const listClassNames = classNames(
    'o-SearchProductListItem__labels u-no-wrap u-position--relative a-list-reset a-list-inline',
    {
      [CLASS_PRODUCT_LABELS_MOBILE]: isMobileDevice,
    },
    additionalClasses
  )

  return (
    <ul
      className={listClassNames}
      data-clientside-hook="searchProductListItemLabels"
    >
      {decorateLabelsWithExtraMobileClasses(labels, mobileLabelsInitialNumber)}
    </ul>
  )
}

SearchProductLabels.propTypes = {
  discountLabel: node,
  displayAboLabel: bool,
  displayNowWidget: bool,
  handleProductAboLabelClick: func,
  mobileLabelsInitialNumber: number,
  offerTypeSae: bool,
  otherLabels: arrayOf(string),
  prescription: bool,
  promotion: string,
  promotionProduct: PromotionProductTypeV2,
  redPointsData: shape({}),
  showDiscountLabel: bool,
  sponsored: bool,
  sponsoredProductLabel: string,
  trackingUrls,
  upid: string,
  additionalClasses: arrayOf(string),
}

SearchProductLabels.defaultProps = {
  discountLabel: null,
  displayAboLabel: false,
  displayNowWidget: false,
  handleProductAboLabelClick: null,
  mobileLabelsInitialNumber: 3,
  offerTypeSae: false,
  otherLabels: [],
  prescription: false,
  promotion: null,
  promotionProduct: null,
  sponsored: false,
  sponsoredProductLabel: null,
  showDiscountLabel: false,
  redPointsData: null,
  trackingUrls: null,
  upid: null,
  additionalClasses: [],
}

export default SearchProductLabels
